import { BaseLogo, ImageRef } from "../../../../core/models/entities/BaseLogo";
import { Color } from "../../../../core/models/entities/LogoOptions";
import { ImageLogoForm } from "../models/ImageLogoForm";
import { hasGenuineHighRes, keepSharpImage } from "./keepSharp";
import { enviroment } from "../../../../enviroment";
import { isUploadKindPending } from "../../../../utils/functions/productPending";
import { isLiveUploadPreviewUrl } from "./uploadPending";
import {
  logoMethodMaxSizesFormPart,
  methodWithAnswerSize,
  sameLogoMethodMaxSizes,
} from "../../../../utils/functions/methodMaxSizeByOrg";

// Flag REACT_APP_LOGO_HEAL_PER_MUTATION - pure helpers for syncing the OPEN
// editor logo from the detail cache when a background heal (GetLogo) lands.
// Only the fields a heal can legitimately improve are patched: the image,
// the awaiting flag, the saved size and the colours (a clone's replayed
// colours arrive with the first GetLogo). Title, method, customer and every
// other user-editable field are left untouched. Returns the SAME reference
// when nothing differs so React state updates stay no-ops - compared by
// VALUE, because every GetLogo response is a fresh object graph (mapped
// arrays and image refs) and a reference check would report a change on
// every poll.

function sameImage(a: ImageRef | undefined, b: ImageRef | undefined): boolean {
  if (a === b) return true;
  if (!a || !b) return false;
  return (
    a.lowResUrl === b.lowResUrl &&
    a.highResUrl === b.highResUrl &&
    a.digitUrl === b.digitUrl &&
    a.trueColorsUrl === b.trueColorsUrl &&
    a.originalUrl === b.originalUrl &&
    (a.highResPending ?? false) === (b.highResPending ?? false)
  );
}

function sameStrings(
  a: readonly string[] | undefined,
  b: readonly string[] | undefined
): boolean {
  if (a === b) return true;
  if (!a || !b || a.length !== b.length) return false;
  return a.every((value, i) => value === b[i]);
}

function sameColorDetails(
  a: readonly Color[] | undefined,
  b: readonly Color[] | undefined
): boolean {
  if (a === b) return true;
  if (!a || !b || a.length !== b.length) return false;
  return a.every(
    (color, i) => color.id === b[i].id && color.isBackup === b[i].isBackup
  );
}

/**
 * `keepSharp` (flag REACT_APP_LOGO_KEEP_SHARP, F1): a detail that still
 * lacks a genuine high-res - the mutation response's own seed, a late poll
 * that beat the render worker - never replaces a sharp bitmap already on the
 * canvas for the same logo; size and colours are still patched. The artwork
 * itself cannot have changed underneath: colour and method changes put the
 * response's fresh thumbnail on the canvas first (never sharp), so from
 * then on every poll patches as before. Default false = today's behaviour.
 */
export function patchLogoFromDetail<T extends BaseLogo>(
  current: T,
  detail: BaseLogo,
  keepSharp = false
): T {
  if (current.id !== detail.id) return current;
  // "Upload stands alone" (flag REACT_APP_UPLOAD_PENDING_ENABLED): while the
  // open logo is a worker-owned UPLOAD its canvas holds the user's own file
  // (raster, seeded as a sharp bitmap) or nothing (vector) - and whatever the
  // re-read carries before the worker has landed is at best the 180 px
  // local thumbnail. The canvas is therefore replaced ONLY by a genuine
  // high-res ("sharp all the time"), whatever the keepSharp flag says; a
  // vector's empty canvas takes any image the landing brings. Flag off = the
  // predicate is false = the keepSharp rule as before.
  const uploadKind = isUploadKindPending(current);
  const image = uploadKind
    ? keepUploadCanvas(current.displayImage, detail.displayImage, current.id)
    : keepSharp
      ? keepSharpImage(current.displayImage, detail.displayImage)
      : detail.displayImage;
  // Flag REACT_APP_CLONE_INSTANT_ENABLED: the clone-upload worker clears
  // uploadPending and writes the clone's own serial - the bounded re-read
  // delivers that here so the editor's badge disappears and its locks open
  // without F5. Flag off = the three fields are never compared or copied.
  const nextSerial = detail.serial || current.serial;
  const uploadSynced =
    !enviroment.cloneInstantEnabled ||
    (!!current.uploadPending === !!detail.uploadPending &&
      (current.uploadPendingSinceUtc ?? null) ===
        (detail.uploadPendingSinceUtc ?? null) &&
      (current.serial ?? "") === (nextSerial ?? ""));
  // Flag REACT_APP_UPLOAD_PENDING_ENABLED: the worker's landing also brings
  // the kind, the METHOD it chose (GetInitialTransferMethod on the supplier's
  // answer: IsDigital -> the digital method, exactly as the synchronous
  // upload does today - the row was saved with the vendor default until
  // then), isDigital / isChangeColor (they gate the editor's method list
  // and colour block), the file name and - for a rejected upload - the
  // reason. Compared and copied ONLY while the open logo is upload-kind
  // pending: a settled logo's method is the user's business (never patched
  // from a poll, as today). Flag off = never compared or copied.
  const uploadKindSynced =
    !uploadKind ||
    ((current.uploadPendingKind ?? null) === (detail.uploadPendingKind ?? null) &&
      (current.uploadFailedReason ?? null) === (detail.uploadFailedReason ?? null) &&
      (current.uploadFailedMessage ?? null) === (detail.uploadFailedMessage ?? null) &&
      (current.uploadFailedUtc ?? null) === (detail.uploadFailedUtc ?? null) &&
      (current.transferMethod?.key ?? null) === (detail.transferMethod?.key ?? null) &&
      !!imageLogoFields(current).isDigital === !!imageLogoFields(detail).isDigital &&
      !!imageLogoFields(current).isChangeColor ===
        !!imageLogoFields(detail).isChangeColor &&
      (imageLogoFields(current).originalFileName ?? null) ===
        (imageLogoFields(detail).originalFileName ?? null));
  const same =
    sameImage(current.displayImage, image) &&
    !!current.awaitingImage === !!detail.awaitingImage &&
    current.widthMm === detail.widthMm &&
    current.heightMm === detail.heightMm &&
    sameStrings(current.colors, detail.colors) &&
    sameColorDetails(current.colorDetails, detail.colorDetails) &&
    uploadSynced &&
    uploadKindSynced;
  if (same) return current;
  const patched: T = {
    ...current,
    displayImage: image as ImageRef,
    awaitingImage: detail.awaitingImage,
    widthMm: detail.widthMm,
    heightMm: detail.heightMm,
    colors: detail.colors,
    colorDetails: detail.colorDetails,
  };
  if (!enviroment.cloneInstantEnabled) return patched;
  const withUpload: T = {
    ...patched,
    uploadPending: detail.uploadPending,
    uploadPendingSinceUtc: detail.uploadPendingSinceUtc,
    serial: nextSerial,
  };
  if (!uploadKind) return withUpload;
  const detailFields = imageLogoFields(detail);
  return {
    ...withUpload,
    uploadPendingKind: detail.uploadPendingKind ?? null,
    uploadFailedReason: detail.uploadFailedReason ?? null,
    uploadFailedMessage: detail.uploadFailedMessage ?? null,
    uploadFailedUtc: detail.uploadFailedUtc ?? null,
    transferMethod: detail.transferMethod ?? current.transferMethod,
    ...(detailFields.isDigital === undefined
      ? {}
      : { isDigital: detailFields.isDigital }),
    ...(detailFields.isChangeColor === undefined
      ? {}
      : { isChangeColor: detailFields.isChangeColor }),
    ...(detailFields.originalFileName === undefined
      ? {}
      : { originalFileName: detailFields.originalFileName }),
  };
}

// The ImageLogo-only fields the worker's landing carries (BaseLogo is the
// shared type of the editor's state).
function imageLogoFields(logo: BaseLogo): {
  isDigital?: boolean;
  isChangeColor?: boolean;
  originalFileName?: string;
} {
  return logo as unknown as {
    isDigital?: boolean;
    isChangeColor?: boolean;
    originalFileName?: string;
  };
}

/**
 * The canvas of a worker-owned upload: `shown` survives whenever it holds a
 * bitmap that can still be painted and `incoming` carries no genuine
 * high-res (the re-read before the landing, or the landing whose high-res
 * is still being stored); a canvas with no bitmap at all (vector upload)
 * takes whatever comes. A blob: bitmap counts only while pendingUploadFiles
 * still holds it for `logoId` (isLiveUploadPreviewUrl) - a revoked object
 * URL is a broken image, and the next detail replaces it whatever it
 * carries (skeptic round 2). Returns one of the two references untouched.
 */
export function keepUploadCanvas(
  shown: ImageRef | undefined,
  incoming: ImageRef | undefined,
  logoId?: number
): ImageRef | undefined {
  if (
    shown?.highResUrl &&
    isLiveUploadPreviewUrl(logoId, shown.highResUrl) &&
    !hasGenuineHighRes(incoming)
  ) {
    return shown;
  }
  return incoming;
}

/**
 * The size fields follow the healed size ONLY while they still show the
 * previously saved size (the user has not typed anything). A field the user
 * edited - even to the same number the server later confirms - is never
 * overwritten mid-edit. Same rule for the colour list (only set by colour
 * change responses, never typed); a list equal by value keeps the form's
 * own array so the form reference stays stable across polls.
 */
export function patchFormFromDetail(
  form: ImageLogoForm,
  previouslySaved: Pick<BaseLogo, "widthMm" | "heightMm" | "colors"> &
    Partial<
      Pick<BaseLogo, "transferMethod" | "uploadPending" | "uploadPendingKind">
    >,
  detail: BaseLogo
): ImageLogoForm {
  if (form.id !== detail.id) return form;
  const untouchedSize =
    form.widthMm === (previouslySaved.widthMm ?? 0) &&
    form.heightMm === (previouslySaved.heightMm ?? 0);
  const untouchedColors = sameStrings(form.colors, previouslySaved.colors);
  // Flag REACT_APP_UPLOAD_PENDING_ENABLED: the method field follows the
  // worker's choice ONLY while the saved logo is a worker-owned upload (the
  // select is locked then, so the field can only hold the draft's saved
  // vendor default) and still shows that saved method - the same "untouched"
  // rule as size and colours. Flag off = the predicate is false = the
  // method is never touched, as today.
  const uploadKind = isUploadKindPending(previouslySaved as BaseLogo);
  const untouchedMethod =
    (form.transferMethod?.key ?? null) ===
    (previouslySaved.transferMethod?.key ?? null);
  const nextMethod =
    uploadKind &&
    untouchedMethod &&
    detail.transferMethod?.key &&
    detail.transferMethod.key !== (form.transferMethod?.key ?? null)
      ? detail.transferMethod
      : form.transferMethod;
  const nextWidth =
    untouchedSize && (detail.widthMm ?? 0) > 0 ? detail.widthMm! : form.widthMm;
  const nextHeight =
    untouchedSize && (detail.heightMm ?? 0) > 0
      ? detail.heightMm!
      : form.heightMm;
  const nextColors =
    untouchedColors && detail.colors && !sameStrings(detail.colors, form.colors)
      ? detail.colors
      : form.colors;
  // Flag REACT_APP_LOGO_METHOD_MAX_SIZE_BY_ORG_ENABLED: the customer's own
  // max sizes follow the detail too (never typed; a logo opened from a list
  // row has none until its first detail lands, and a detail without the
  // list means the customer has no override any more). A list equal by
  // value keeps the form's own array. So does the detail's effective limit
  // of the form's CURRENT method (transferMethod.size, same key): a list
  // row's size is resolved without the customer in some flows, so until
  // the detail lands the form's cap for typing, dragging and "Change size"
  // may be the general one - the detail is the API's answer for THIS logo
  // (skeptic: a customer with a LARGER limit could not type above the
  // general one). A method picked in the form (another key, unsaved) is
  // never touched. Flag off = the form never gets the key and nothing is
  // compared.
  if (enviroment.logoMethodMaxSizeByOrgEnabled) {
    const detailPart = logoMethodMaxSizesFormPart(detail);
    const maxSizesChanged = !sameLogoMethodMaxSizes(
      form.methodMaxSizes,
      detailPart.methodMaxSizes,
    );
    const nextSizedMethod =
      nextMethod === form.transferMethod
        ? methodWithAnswerSize(form.transferMethod, detail.transferMethod)
        : nextMethod;
    if (
      !maxSizesChanged &&
      nextWidth === form.widthMm &&
      nextHeight === form.heightMm &&
      nextColors === form.colors &&
      nextSizedMethod === form.transferMethod
    ) {
      return form;
    }
    const { methodMaxSizes: formMaxSizes, ...rest } = form;
    const nextMaxSizes = maxSizesChanged
      ? detailPart.methodMaxSizes
      : formMaxSizes;
    return {
      ...rest,
      widthMm: nextWidth,
      heightMm: nextHeight,
      colors: nextColors,
      transferMethod: nextSizedMethod,
      ...(nextMaxSizes ? { methodMaxSizes: nextMaxSizes } : {}),
    };
  }
  if (
    nextWidth === form.widthMm &&
    nextHeight === form.heightMm &&
    nextColors === form.colors &&
    nextMethod === form.transferMethod
  ) {
    return form;
  }
  return {
    ...form,
    widthMm: nextWidth,
    heightMm: nextHeight,
    colors: nextColors,
    transferMethod: nextMethod,
  };
}
